import type { Permission } from '@/stores/auth-store'

/**
 * Permission groups for easier management
 * Format: 'resource:action' (matches backend seeder)
 */
export const PERMISSIONS = {
  USERS: {
    READ: 'users:read',
    WRITE: 'users:write',
    DELETE: 'users:delete',
    MANAGE: 'users:manage',
    MANAGE_ROLES: 'users:manage-roles',
  },
  STAFF: {
    READ: 'staff:read',
    CREATE: 'staff:create',
    WRITE: 'staff:write',
    DELETE: 'staff:delete',
    MANAGE: 'staff:manage',
    MANAGE_ROLES: 'staff:manage-roles',
  },
  ROLES: {
    READ: 'roles:read',
    WRITE: 'roles:write',
    DELETE: 'roles:delete',
    MANAGE: 'roles:manage',
  },
  PERMISSIONS: {
    READ: 'permissions:read',
    WRITE: 'permissions:write',
    DELETE: 'permissions:delete',
    MANAGE: 'permissions:manage',
  },
  UPLOAD: { WRITE: 'upload:write' },
  MEMES: {
    READ: 'memes:read',
    SCRAPE: 'memes:scrape',
    POST: 'memes:post',
    AUTO_POST: 'memes:auto-post',
    DELETE: 'memes:delete',
  },
  SOCIAL_MEDIA: {
    POST: 'social-media:post',
    VIEW_HISTORY: 'social-media:view-history',
    MANAGE: 'social-media:manage',
  },
  MODERATE: { REPORTS: 'moderate:reports', CONTENT: 'moderate:content' },
  POST: { MANAGE: 'post:manage' },
  POSTS: {
    READ: 'posts:read',
    WRITE: 'posts:write',
    DELETE: 'posts:delete',
    MODERATE: 'posts:moderate',
  },
  TAGS: { READ: 'tags:read', WRITE: 'tags:write', DELETE: 'tags:delete' },
  SETTINGS: { READ: 'settings:read', WRITE: 'settings:write' },
  SUPERADMIN: { MANAGE: 'superadmin:manage' },
  SYSTEM: { ADMIN: 'system:admin' },
} as const satisfies Record<string, Record<string, Permission>>

// Example role constants (actual roles are dynamic from backend)
export const ROLES = {
  ADMIN: 'ADMIN',
  MODERATOR: 'MODERATOR',
  USER: 'USER',
} as const

// Helper to get all permissions in a category
export const getAllPermissionsInCategory = (
  category: keyof typeof PERMISSIONS
): Permission[] => Object.values(PERMISSIONS[category])

// Resource-permission mapping (for UI grouping)
export const RESOURCE_PERMISSIONS = {
  users: getAllPermissionsInCategory('USERS'),
  staff: getAllPermissionsInCategory('STAFF'),
  roles: getAllPermissionsInCategory('ROLES'),
  permissions: getAllPermissionsInCategory('PERMISSIONS'),
  upload: [PERMISSIONS.UPLOAD.WRITE],
  memes: [
    PERMISSIONS.MEMES.READ,
    PERMISSIONS.MEMES.SCRAPE,
    PERMISSIONS.MEMES.POST,
    PERMISSIONS.MEMES.AUTO_POST,
  ],
  moderate: getAllPermissionsInCategory('MODERATE'),
  post: [PERMISSIONS.POST.MANAGE],
  posts: getAllPermissionsInCategory('POSTS'),
  tags: getAllPermissionsInCategory('TAGS'),
  settings: getAllPermissionsInCategory('SETTINGS'),
} as const

const capitalize = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1)

// Get readable permission name
export const getPermissionLabel = (permission: Permission): string => {
  const [resource, action] = permission.split(':')
  return `${capitalize(resource)} - ${action.split('-').map(capitalize).join(' ')}`
}

// Get readable role name
export const getRoleLabel = (role: string): string =>
  role
    .split('_')
    .map((word) => capitalize(word.toLowerCase()))
    .join(' ')
